import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import { motion } from 'framer-motion';

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');

  // Fetch gallery items from Firebase
  useEffect(() => {
    const galleryRef = ref(db, 'gallery');
    onValue(galleryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const items = Object.entries(data).map(([id, item]) => ({ id, ...item }));
        setGalleryItems(items);
      }
    });
  }, []);

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalImage(null);
    document.body.style.overflow = 'auto';
  };

  // Get unique categories from gallery items
  const categories = ['All', ...new Set(galleryItems.map(item => item.category))];
  const filteredItems = activeFilter === 'All' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeFilter);

  return (
    <>
      <style>{`
        .gallery-container {
          padding: 10px 20px;
          background: linear-gradient(to bottom, #f8f9fa, #e9f5ff);
          min-height: 100vh;
          font-family: 'Poppins', sans-serif;
        }

        .gallery-title {
          font-size: 2.5rem;
          font-weight: 700;
          color: #023e8a;
          margin-bottom: 20px;
          text-align: center;
          position: relative;
          display: inline-block;
          width: 100%;
        }

        .gallery-title:after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: linear-gradient(to right, #3498db, #9b59b6);
          margin: 10px auto 30px;
          border-radius: 2px;
        }

        .filter-buttons {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 30px;
        }

        .filter-btn {
          padding: 8px 20px;
          border: none;
          background: #e0e0e0;
          color: #555;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .filter-btn:hover {
          background: #3498db;
          color: white;
          transform: translateY(-2px);
        }

        .filter-btn.active {
          background: linear-gradient(to right, #3498db, #9b59b6);
          color: white;
          box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 25px;
          padding: 0 20px;
        }

        .gallery-card {
          position: relative;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          border: 2px solid #0e0e0fff;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0, 1);
          aspect-ratio: 4/3;
        }

        .gallery-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
        }

        .gallery-card:hover .card-img {
          transform: scale(1.1);
          filter: brightness(0.7);
        }

        .gallery-card:hover .card-content {
          opacity: 1;
          transform: translateY(0);
        }

        .card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.4s ease;
        }

        .card-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 20px;
          background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%);
          color: white;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.3s ease;
        }

        .card-caption {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 5px;
        }

        .card-category {
          font-size: 0.9rem;
          opacity: 0.8;
          display: inline-block;
          padding: 3px 10px;
          background: rgba(255,255,255,0.2);
          border-radius: 20px;
        }

        .modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          backdrop-filter: blur(5px);
        }

        .modal-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
          animation: zoomIn 0.3s ease;
        }

        @keyframes zoomIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .modal-content img {
          width: 100%;
          height: auto;
          max-height: 80vh;
          object-fit: contain;
          border-radius: 10px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
        }

        .close-btn {
          position: absolute;
          top: 30px;
          right: 30px;
          font-size: 2rem;
          color: white;
          cursor: pointer;
          z-index: 1001;
          transition: transform 0.2s ease;
        }

        .close-btn:hover {
          transform: rotate(90deg);
        }

        .modal-caption {
          position: absolute;
          bottom: 20px;
          left: 0;
          right: 0;
          text-align: center;
          color: white;
          font-size: 1.2rem;
          padding: 15px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 0 0 10px 10px;
        }

        @media (max-width: 768px) {
          .gallery-title {
            font-size: 2rem;
          }
          
          .gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 15px;
          }
          
          .filter-buttons {
            gap: 8px;
          }
          
          .filter-btn {
            padding: 6px 15px;
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr;
          }
          
          .gallery-card {
            aspect-ratio: 16/9;
          }
          
          .card-content {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="gallery-container">
        <motion.h2 
          className="gallery-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Memorable Moments Gallery
        </motion.h2>

        <div className="filter-buttons">
          {categories.map((category, index) => (
            <button
              key={index}
              className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
              onClick={() => setActiveFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No gallery items found. Check back later!
          </div>
        ) : (
          <motion.div 
            className="gallery-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
          >
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                className="gallery-card"
                whileHover={{ scale: 1.03 }}
                onClick={() => openModal(item.imageUrl)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={item.imageUrl} 
                  alt={item.caption} 
                  className="card-img" 
                  loading="lazy"
                />
                <div className="card-content">
                  <h3 className="card-caption">{item.caption}</h3>
                  <span className="card-category">{item.category}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {modalOpen && (
        <motion.div 
          className="modal" 
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="close-btn" onClick={closeModal}>&times;</div>
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <img src={modalImage} alt="Full View" />
            <div className="modal-caption">
              {filteredItems.find(item => item.imageUrl === modalImage)?.caption}
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Gallery;